import { useMemo } from 'react';
import { roundToTwoDecimals } from '../../../../../helpers/Units';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActivePlan } from '../plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../plan-details/useRetrieveActivePlanDrivers';

export interface UseRetrieveAnalyticsPlanCostResponse {
  totalCost: number;
  averageRouteCost: number;
  averageStopCost: number;
  vehiclesWithoutCost: number;
}

export const useRetrieveAnalyticsPlanCosts = (): UseRetrieveAnalyticsPlanCostResponse => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();
  const { data: activePlan } = useRetrieveActivePlan();

  const { totalCost, averageRouteCost, averageStopCost, vehiclesWithoutCost } =
    useMemo<UseRetrieveAnalyticsPlanCostResponse>(() => {
      let totalCost = 0;
      let averageRouteCost = 0;
      let averageStopCost = 0;
      let vehiclesWithoutCost = 0;

      if (!activePlan || !activeProject || !planDrivers) {
        return {
          totalCost,
          averageRouteCost,
          averageStopCost,
          vehiclesWithoutCost,
        };
      }

      totalCost = roundToTwoDecimals(planDrivers.reduce((prev, driver) => prev + driver.getTotalPriceUsingKM(), 0));

      averageRouteCost = roundToTwoDecimals(totalCost / (planDrivers.length || 1));

      averageStopCost = roundToTwoDecimals(totalCost / (activePlan.total_stops || 1));

      vehiclesWithoutCost = planDrivers?.filter(
        (driver) => driver.price_per_distance === undefined && driver.price_per_minute === undefined
      ).length;

      return {
        totalCost,
        averageRouteCost,
        averageStopCost,
        vehiclesWithoutCost,
      };
    }, [activePlan, planDrivers, activeProject]);

  return { totalCost, averageRouteCost, averageStopCost, vehiclesWithoutCost };
};
